/**
 * @module app.QueryController
 */
import {unlistenByKey, listen} from 'ol/events';
import appBase from './index.js';
import {
  capitalize, isSingleWinterLayer, layerIndex,
  lonLatToMapProjection, lv95ToMapProjection,
  mapProjectionToLonLat,
  mapProjectionToLv95, NO_HTTP_CREDENTIALS,
  objectFilter,
  objectForEach,
  objectSome, reprojectInPlace
} from './utils.js';
import appApplyHelper from './ApplyHelper.js';
import appAppOverlay from './AppOverlay.js';
import olFeature from 'ol/Feature.js';
import olFormatGeoJSON from 'ol/format/GeoJSON.js';
import {appendParams} from 'ol/uri';
import {isEmpty} from "ol/obj";
import {createOrUpdateFromCoordinate, buffer as olExtentBuffer} from "ol/extent";
import {pickOnTerrainOrEllipsoid, computeBoundingBoxAtTarget} from "ol-cesium/src/olcs/core.ts";
import {QUERY_HIGHLIGHT_ID, includeBbox, replaceSpecialCharacters} from "./utils.js";
import {FEATURE_CHOICE_CLASS_NAME, MOUNTAINEERING_TYPE_TO_LAYER} from './constants.js';


/**
 * @const
 * @type {!Object.<string, string>}
 */
export const LAND_COLORS_CLASS = {
  'veloland': 'velo-choice',
  'wanderland': 'wander-choice',
  'mtbland': 'mountainbike-choice',
  'skatingland': 'skating-choice',
  'kanuland': 'kanu-choice',
  'crosscountry': 'crosscountry-choice',
  'winterhiking': 'winterhiking-choice',
  'snowshoe': 'snowshoe-choice',
  'sledging': 'sledging-choice',
  'services': 'service-choice',
  'stations': 'bbs-choice',
  'slowup': 'slowup-choice',
  'swisspark': 'swisspark-choice',
  'mountaineering': 'mountaineering-choice'
};


/**
 * @const
 * @type {!Object.<string, string>}
 */
export const WS_ALIASES = {
  'accomodation': 'accomodation',
  'alpentaxi': 'alpentaxi',
  'cycleservice': 'cycleservice',
  'places': 'place',
  'poi': 'sightseeing',
  'rental': 'velorental',
  'stations': 'stations',
  'veloland': 'velo',
  'wanderland': 'wander',
  'mtbland': 'mountainbike',
  'skatingland': 'skating',
  'kanuland': 'kanu',
  'crosscountry': 'crosscountry',
  'winterhiking': 'winterhiking',
  'snowshoe': 'snowshoe',
  'sledging': 'sledging',
  'services': 'services',
  'all': 'all',
  'national': 'national',
  'regional': 'regional',
  'local': 'lokal',
  'tipp': 'tipp',
  'handicap': 'handicap'
};


/**
 * @const
 * @type {!Object.<string, string>}
 */
export const WS_I18N = {
  'backpacker': 'Backpackers | Hostel',
  'bedbreakfast': 'Bed Breakfast',
  'campingsite': 'Camping site',
  'farmaccom': 'Farm accommodation',
  'groupaccom': 'Group accommodation',
  'hotel': 'Hotel',
  'mountainhut': 'SAC | Mountain hut',
  'sleepingstraw': 'Sleeping in straw',
  'vacationapptmt': 'Vacation apartment',
  'youthhostel': 'Youth hostel',
  'place': 'places',
  'velo': 'veloland',
  'wander': 'wanderland',
  'mountainbike': 'mountainbikeland',
  'skating': 'skatingland',
  'kanu': 'kanuland',
  'crosscountry': 'crosscountry',
  'winterhiking': 'winterhiking',
  'snowshoe': 'snowshoe',
  'sledging': 'sledging',
  'train': 'Train',
  'bus': 'Bus',
  'trambus': 'Tram / Bus',
  'boat': 'Boat',
  'cableway': 'Aerial cableway',
  'chairlift': 'Chairlift',
  'telecabin': 'Telecabin',
  'funicular': 'Funicular railway',
  'alpentaxi': 'Alpentaxi'
};


/**
 * @const
 * @type {!Array.<string>}
 */
export const SKIP_CATEGORIES = ['detour'];


/**
 * @const
 * @type {!Object.<string, string>}
 */
export const LAYERNAME_TO_BODID = {
  'WildlifeArea': 'ch.bafu.wrz-wildruhezonen_portal',
  'WildlifeReserve': 'ch.bafu.wrz-jagdbanngebiete_select',
  'AlpguardDog': 'ch.bafu.alpweiden-herdenschutzhunde'
};

/**
 * land name to website (https://www.schweizmobil.ch) expected land name.
 * If a land is not present; the value is the same.
 * @const
 * @type {!Object.<string, string>}
 */
export const LAND_FOR_WEBSITE = {
  'mtbland': 'mountainbikeland',
  'winterhiking': 'winterwandern',
  'snowshoe': 'schneeschuhwandern',
  'crosscountry': 'langlaufen',
  'sledging': 'schlitteln'
};

/**
 * @const
 * @type {!Object.<string, string>}
 */
export const BODID_TO_LAYERNAME = {
  'ch.bafu.wrz-wildruhezonen_portal': 'WildlifeArea',
  'ch.bafu.wrz-jagdbanngebiete_select': 'WildlifeReserve',
  'ch.bafu.alpweiden-herdenschutzhunde': 'AlpguardDog'
};

/**
 * @const
 * @type {!Array.<string>}
 */
export const BODID_USING_HTML_POPUP = [
  'ch.bafu.alpweiden-herdenschutzhunde',
];


/**
 * @constructor
 * @param {angular.Scope} $scope Angular scope.
 * @param {jQuery} $element Element.
 * @param {angular.$http} $http Angular http service.
 * @param {string} apiUrl API url prefix.
 * @param {angularGettext.Catalog} gettextCatalog Gettext catalog.
 * @param {app.MapService} appMapService
 * @param {app.HighlightFeature} appHighlightFeature
 * @param {angular.$timeout} $timeout Angular timeout service.
 * @param {angular.$q} $q Angular Q service.
 * @param {!app.query.state.Service} appQueryStateService
 * @param {app.detour.Service} appDetourService
 * @param {app.CesiumLoader} appCesiumLoader
 * @param {{photos: Array<Object>, rotate_hint: boolean}} appCarouselPhotos
 * @param {app.waiting} appWaiting
 * @ngInject
 * @struct
 */
class QueryController {
  constructor(
    $scope,
    $element,
    $http,
    apiUrl,
    gettextCatalog,
    appMapService,
    appHighlightFeature,
    $timeout,
    $q,
    appQueryStateService,
    appDetourService,
    appCesiumLoader,
    appCarouselPhotos,
    appWaiting
  ) {

    /**
     * @type {app.MainController}
     * @private
     */
    this.mainCtrl_ = $scope.$root['mainCtrl'];

    /**
     * @type {app.CesiumLoader}
     * @private
     */
    this.cesium_ = appCesiumLoader;

    /**
     * @type {boolean}
     * @export
     */
    this.active;

    /**
     * @private
     */
    this.scope_ = $scope;

    /**
     * @private
     */
    this.http_ = $http;

    /**
     * @private
     */
    this.timeout_ = $timeout;

    /**
     * @private
     */
    this.q_ = $q;

    /**
     * @private
     */
    this.queryStateService_ = appQueryStateService;

    /**
     * @private
     */
    this.appDetourService_ = appDetourService;

    this.gettextCatalog = gettextCatalog;

    /**
     * @private
     */
    this.appCarouselPhotos_ = appCarouselPhotos;

    /**
     * @private
     */
    this.appWaiting_ = appWaiting;

    /**
     * @type {!ol.Feature}
     * @private
     */
    this.highlightedFeature_ = new olFeature();

    /**
     * @type {app.HighlightFeature}
     * @private
     */
    this.appHighlight_ = appHighlightFeature;
    console.assert(appHighlightFeature.initialized);
    this.appHighlight_.addFeature(this.highlightedFeature_, QUERY_HIGHLIGHT_ID, {
      'isProtected': true
    });

    /**
     * @type {ol.Map}
     * @private
     */
    this.map_ = appMapService.map;

    /**
     * @type {ol.View}
     * @private
     */
    this.view_ = this.map_.getView();

    /**
     * @type {AppOverlay}
     * @private
     */
    this.popup_ = new appAppOverlay(/** @type {olx.OverlayOptions} */ ({
      element: /** @type {Element} */ ($element.get(0)),
      autoPan: true,
      autoPanAnimation: {
        duration: 250
      }
    }),
    appCesiumLoader
    );
    this.map_.addOverlay(this.popup_);

    /**
     * @type {Array.<Object>}
     * @export
     */
    this.features = [];

    /**
     * @type {ol.format.GeoJSON}
     * @private
     */
    this.format_ = new olFormatGeoJSON();

    /**
     * @type {ol.Coordinate}
     * @private
     */
    this.click2DCoordinates_ = null;

    /**
     * @type {number}
     * @private
     */
    this.tolerance_ = 20;

    /**
     * @type {number}
     * @private
     */
    this.wildlifeTolerance_ = 5;

    /**
     * Maximum number of POIs to query from the server.
     * @private
     */
    this.maxPoiQuery_ = 15;

    /**
     * @type {string}
     * @private
     */
    this.queryUrl_ = appendParams(`${apiUrl}4/feature/query`, {
      'restriction': this.maxPoiQuery_,
      'attributes': 'yes',
      'type': 'etappen',
      'land': 'all',
      'category': 'all',
      'simplify': '5',
      'translated': true,
    });


    /**
     * @type {string}
     * @private
     */
    this.swisstopoQueryUrl_ = appendParams('https://api3.geo.admin.ch/rest/services/api/MapServer/identify', {
      'geometryType': 'esriGeometryEnvelope',
      'imageDisplay': '500,600,96',
      'mapExtent': '548945,147956,549402,148103',
      'tolerance': 0,
      'geometryFormat': 'geojson',
      'sr': 2056
    });

    /**
     * @type {string}
     * @private
     */
    this.multilayersUrl_ = apiUrl + '4/query/layer_pois_by_ids';

    /**
     * @type {Array.<EventsKey>}
     * @private
     */
    this.listenerKeys_ = [];

    const h = new appApplyHelper($scope);

    $scope.$watch(function() {
      return this.active;
    }.bind(this), function(active) {
      if (active) {
        console.assert(this.listenerKeys_.length === 0);
        this.listenerKeys_.push(
          listen(this.map_, 'singleclick', h.eventCb(this.onClick_), this),
          listen(this.map_, 'pointermove', h.eventCb(this.onPointerMove_), this)
        );
      } else {
        this.listenerKeys_.forEach(unlistenByKey);
        this.listenerKeys_.length = 0;
      }
    }.bind(this));

    $element.on('click', `.${FEATURE_CHOICE_CLASS_NAME}`, h.eventCb(this.onSelectFeature_, this));
    $element.on('mouseenter', `.${FEATURE_CHOICE_CLASS_NAME}`, h.eventCb(this.setHighlight_, this));

    this.scope_.$on('disableQuerying', () => {
      this.closePopup();
    });

  }

  /**
   * @param {Object} properties
   * @private
   */
  normalizeProperties_(properties) {
    // add missing 'http://'
    ['url1_link', 'url2_link', 'url', 'photo', 'file'].forEach((key) => {
      if (properties[key] &&
          !properties[key].startsWith('http://') &&
          !properties[key].startsWith('https://')) {
        properties[key] = 'http://' + properties[key];
      }
    });
  }

  /**
   * @private
   * @param {jQuery.Event} evt
   */
  setHighlight_(evt) {
    let feature, i, geom, kind;
    const id = parseInt($(evt.currentTarget).data('id'), 10);

    for (i = 0; i < this.features.length; i++) {
      if (this.features[i]['id'] === id) {
        feature = this.features[i];
        break;
      }
    }
    if (feature['properties']['category'] == 'detour') {
      this.highlightedFeature_.setGeometry(undefined);
      this.appDetourService_.highlightFeatures(feature['properties']['id']);
    } else {
      geom = this.format_.readGeometry(feature['geometry']);
      if (geom) {
        const sl_hlname = feature['properties']['sl_hlname'];
        if (feature['geometry']['type'] === 'MultiLineString') {
          if (sl_hlname == 'slowup') {
            kind = sl_hlname;
          } else {
            kind = 'transparent';
          }
        } else {
          // special case for alpentaxi
          const category_alpentaxi = feature['properties']['category_alpentaxi'];
          if (category_alpentaxi) {
            kind = category_alpentaxi;
          } else {
            kind = sl_hlname;
          }
        }
        this.appHighlight_.setFeature(this.highlightedFeature_, {
          'geom': geom,
          'kind': kind,
          'signal_way': feature['properties']['signal_way'],
          'route_signal_way': feature['properties']['route_signal_way']
        });
      }
      // remove highlight on detour, if any
      this.appDetourService_.unhighlightFeatures();
    }
  }

  /**
   * @private
   */
  unsetHighlight_() {
    this.appDetourService_.unhighlightFeatures();
    this.highlightedFeature_.setGeometry(undefined);
  }

  /**
   * @private
   * @param {Array<number>} pixel
   * @param {string} type
   * @param {Array.<ol.Feature>} features
   */
  getMapFeatures_(pixel, type, features) {
    // Pick selected track
    this.map_.forEachFeatureAtPixel(pixel, function(feature, layer) {
      if (layer && layer.get('type') === type) {
        console.assert(feature instanceof olFeature);
        const clusterFeatures = /** @type{Array<ol.Feature>} */ (feature.get('features'));
        if (clusterFeatures) {
          features.push(...clusterFeatures);
        } else {
          features.push(feature);
        }
      }
    }, this);
  }

  /**
   * @private
   * @param {Array<number>} pixel
   * @param {Array.<ol.Feature>} trackFeatures
   * @param {Object.<string, Object.<string, number>>} poi3dFeatureIds
   */
  getGlobeFeatures_(pixel, trackFeatures, poi3dFeatureIds) {
    console.assert(this.cesium_.is3dEnabled());
    const ol3d = this.cesium_.ol3d;
    const poiLayers3d = this.cesium_.poiLayers3d;
    const scene = ol3d.getCesiumScene();

    const position = new Cesium.Cartesian2(pixel[0], pixel[1]);
    const cesiumObjectArray = scene.drillPick(position);
    for (let csi = 0; csi < cesiumObjectArray.length; ++csi) {
      const cesiumObject = cesiumObjectArray[csi];

      const feature = cesiumObject.primitive.olFeature;
      const layer = cesiumObject.primitive.olLayer;
      const type = (feature && layer) ? layer.get('type') : '';
      if (type === 'selected_tracks') {
        trackFeatures.push(feature);
      } else if (type === '3dpoi') {
        const code = /** @type {string} */ (layer.get('code')); // eg. SightSeeing
        let found = poi3dFeatureIds[code];
        if (!found) {
          found = poi3dFeatureIds[code] = {};
        }
        poiLayers3d.findChildrenIds(code, feature, scene, found);
      }
    }
  }

  /**
   * Limit the number of 3D POIs and also prevent "duplicates".
   * Duplicates may occur when a POI has several tags.
   * @private
   * @param {Object.<string, Object.<string, number>>} poi3dFeatureIds
   * @return {Object.<string, Object.<string, number>>}
   */
  truncatePoi3dFeatureIds_(poi3dFeatureIds) {
    const max = this.maxPoiQuery_;
    let i = 0;
    const output = {};
    const done = {};
    objectForEach(poi3dFeatureIds, function(element, key) {
      const keptIds = output[key] = {};
      objectSome(element, function(value, id) {
        ++i;
        if (i > max) {
          return true;
        }
        if (!done[id]) {
          // keep if the POI is not a duplicate
          done[id] = true;
          keptIds[id] = value;
        }
        return false;
      });
    });
    return output;
  }

  /**
   * @param {ol.Coordinate} coordinates
   * @return {Cesium.Cartesian3}
   */
  swissToCartesian(coordinates) {
    const cooWGS84 = mapProjectionToLonLat(coordinates);
    const cartographic = new Cesium.Cartographic(
      Cesium.Math.toRadians(cooWGS84[0]),
      Cesium.Math.toRadians(cooWGS84[1]),
      0);
    const ol3d = this.cesium_.ol3d;
    const globe = ol3d.getCesiumScene().globe;
    const height = globe.getHeight(cartographic);
    cartographic.height = height || 0;

    return Cesium.Ellipsoid.WGS84.cartographicToCartesian(cartographic);
  }

  /**
   * @param {Cesium.Cartesian3} cartesian
   * @return {ol.Coordinate}
   */
  cartesianToSwiss(cartesian) {
    const carto = Cesium.Ellipsoid.WGS84.cartesianToCartographic(cartesian);
    const coordinates = [
      Cesium.Math.toDegrees(carto.longitude),
      Cesium.Math.toDegrees(carto.latitude)
    ];
    return lonLatToMapProjection(coordinates);
  }

  /**
   * @private
   * @param {ol.MapBrowserPointerEvent} evt
   */
  onClick_(evt) {
    if (this.queryStateService_.isQueryEnabled()) {
      const trackFeatures = [];
      const photoFeatures = [];
      let poi3dFeatureIds = {};
      this.closePopup();
      let bbox;
      let wildlifeToleranceBbox = undefined;
      const ol3d = this.cesium_.ol3d;
      if (ol3d && ol3d.getEnabled()) {
        const scene = ol3d.getCesiumScene();
        const csPixel = new Cesium.Cartesian2(evt.pixel[0], evt.pixel[1]);
        const cartesian = scene.pickPosition(csPixel);
        this.popup_.setCartesianPosition(cartesian);
        this.click2DCoordinates_ = this.cartesianToSwiss(cartesian);
        this.getGlobeFeatures_(evt.pixel, trackFeatures, poi3dFeatureIds);
        poi3dFeatureIds = this.truncatePoi3dFeatureIds_(poi3dFeatureIds);
        bbox = this.getCsBBoxFromPixel_(evt.pixel, this.tolerance_);
        wildlifeToleranceBbox = this.getCsBBoxFromPixel_(evt.pixel, this.wildlifeTolerance_);
      } else {
        this.click2DCoordinates_ = evt.coordinate;
        if (ol3d) {
          const coordinate3D = this.swissToCartesian(this.click2DCoordinates_);
          this.popup_.setCartesianPosition(coordinate3D);
        }
        this.getMapFeatures_(evt.pixel, 'selected_tracks', trackFeatures);
        this.getMapFeatures_(evt.pixel, 'photo_layer', photoFeatures);
        bbox = this.getOlBBoxFromCoordinates_(this.click2DCoordinates_, this.tolerance_);
        wildlifeToleranceBbox = this.getOlBBoxFromCoordinates_(this.click2DCoordinates_, this.wildlifeTolerance_);
      }

      // the short list is not displayed if we have photo(s) from the photo layer.
      if (photoFeatures.length > 0) {
        this.appCarouselPhotos_.photos = photoFeatures.map(feature => feature.getProperties());
        this.appCarouselPhotos_.rotate_hint = true;
      } else {
        this.getFeatures_(bbox, trackFeatures, poi3dFeatureIds, wildlifeToleranceBbox);
      }
    }
  }

  /**
   * @private
   * @param {ol.MapBrowserPointerEvent} evt
   */
  onPointerMove_(evt) {
    const is3dEnabled = this.cesium_.is3dEnabled();
    if (evt.dragging && (!is3dEnabled)) {
      this.closePopup();
    }
  }

  /**
   * @private
   * @param {ol.Coordinate} cc
   * @param {number} tolerance
   * @return {ol.Extent}
   */
  getOlBBoxFromCoordinates_(cc, tolerance) {
    const bbox = createOrUpdateFromCoordinate(cc);
    const buffer = this.view_.getResolution() * tolerance;
    olExtentBuffer(bbox, buffer, bbox);
    return bbox.map(function(value) {
      return parseInt(value, 10);
    });
  }

  /**
   * @private
   * @param {Array.<number>} olPixel
   * @param {number} tolerance
   * @return {ol.Extent}
   */
  getCsBBoxFromPixel_(olPixel, tolerance) {
    const pixel = new Cesium.Cartesian2(olPixel[0], olPixel[1]);
    console.assert(this.cesium_.is3dEnabled());
    const scene = this.cesium_.ol3d.getCesiumScene();
    const target = pickOnTerrainOrEllipsoid(scene, pixel);
    if (!target) {
      return null;
    }
    const csExt = computeBoundingBoxAtTarget(scene, target, tolerance);
    const olExtent = [].concat(...csExt.map(carto => lonLatToMapProjection([
      Cesium.Math.toDegrees(carto.longitude),
      Cesium.Math.toDegrees(carto.latitude)
    ])));
    return olExtent;
  }

  /**
   * @private
   * @param {ol.Extent} bbox
   * @param {Array.<ol.Feature>} trackFeatures
   * @param {Object.<string, !Object.<string, number>>} poi3dFeatureIds
   * @param {ol.Extent} wildlifeToleranceBbox
   */
  getFeatures_(bbox, trackFeatures, poi3dFeatureIds, wildlifeToleranceBbox) {
    const is3dEnabled = this.cesium_.is3dEnabled();
    let layers = objectFilter(this.mainCtrl_.activeLayers(), (value, key) => {
      return value && (!is3dEnabled || this.cesium_.poi3dFilterFunction(key));
    });

    if (isEmpty(layers) && isEmpty(poi3dFeatureIds)) {
      // no layer to query, only display the map features
      if (trackFeatures.length > 0) {
        this.fillShortlist_([], trackFeatures);
      }
      return;
    }

    const urls = [];
    const lang = this.gettextCatalog.currentLanguage;
    const slowUpPoisOnly = poi3dFeatureIds['Slowup'] && Object.keys(poi3dFeatureIds).length === 1;
    if (!isEmpty(poi3dFeatureIds) && !slowUpPoisOnly) {
      layers = '';
      objectForEach(poi3dFeatureIds, (ids, layerCode) => {
        if (layerCode !== 'Slowup') {
          layers += `&${layerCode}=${Object.keys(ids).join(',')}`;
        }
      });
      layers = layers.substring(1);
      const multilayersUrl = appendParams(this.multilayersUrl_, {
        'language': lang,
        'layers': layers
      });
      urls.push(multilayersUrl);
    } else {
      const layersNames = slowUpPoisOnly ? ['Slowup'] : Object.keys(layers);
      const queryUrl = appendParams(this.queryUrl_, {
        'language': lang,
        'layers': layersNames.join(','),
        'bbox': bbox.join(',')
      });
      urls.push(queryUrl);

      const swisstopoLayers = layersNames.reduce((acc, name) => {
        const bodId = LAYERNAME_TO_BODID[name];
        if (bodId) {
          acc.push(bodId);
        }
        return acc;
      }, []);

      if (swisstopoLayers.length > 0) {
        const bboxLv95 =
            mapProjectionToLv95([bbox[0], bbox[1]]).concat(
              mapProjectionToLv95([bbox[2], bbox[3]])
            );
        const swisstopoQueryUrl = appendParams(this.swisstopoQueryUrl_, {
          'lang': lang,
          'layers': 'all:' + swisstopoLayers.join(','),
          'geometry': bboxLv95.join(',')
        });
        urls.push(swisstopoQueryUrl);
      }
    }

    this.appWaiting_.increment();
    this.q_.all(urls.map((url) => this.http_.get(url, NO_HTTP_CREDENTIALS)))
      .then((responses) => {
        let features = [];
        for (let i = 0; i < responses.length; i++) {
          const data = responses[i].data;
          if (data.features) {
            features = features.concat(data.features);
          } else if (data['results']) {
            const reprojectFeatures = data['results'];
            reprojectFeatures.forEach((reprojectFeature) => {
              reprojectInPlace(reprojectFeature, lv95ToMapProjection);
            });
            features = features.concat(reprojectFeatures);
          }
        }

        if (wildlifeToleranceBbox) {
          const bboxLv95 =
              mapProjectionToLv95([wildlifeToleranceBbox[0], wildlifeToleranceBbox[1]]).concat(
                mapProjectionToLv95([wildlifeToleranceBbox[2], wildlifeToleranceBbox[3]])
              );
          features = this.applyLowerTolerance(features, bboxLv95);
        }
        this.fillShortlist_(features, trackFeatures);
      }).catch((error) => {
        bootbox.alert({
          title: this.gettextCatalog.getString('Query failed'),
          message: this.gettextCatalog.getString('connect to the server error')
        });
        console.error(error);
      }).finally(() => {
        this.appWaiting_.decrement();
      });
  }

  applyLowerTolerance(features, bbox) {
    return features.filter(feature => {
      if (feature.properties.type === 'wildlife' || !feature.bbox) {
        return true;
      }
      return includeBbox(bbox, feature.bbox);
    });
  }

  /**
   * @private
   * @param {!Array.<Object>} rawFeatures
   * @param {Array.<ol.Feature>} trackFeatures
   */
  fillShortlist_(rawFeatures, trackFeatures) {
    this.features.length = 0;
    const filteredFeatures = {};

    for (let i = 0; i < rawFeatures.length; i++) {
      const feature = rawFeatures[i];
      this.normalizeProperties_(feature['properties']);
      const category = feature['properties']['category'];
      if (!SKIP_CATEGORIES.includes(category)) {
        feature['properties'] = angular.extend(
          feature['properties'],
          this.handleFeatureProperties_(feature)
        );
        this.features.push(feature);
      } else {
        if (!filteredFeatures[category]) {
          filteredFeatures[category] = [];
        }
        filteredFeatures[category].push(feature);
      }

      const layerBodId = feature['layerBodId'];
      if (BODID_USING_HTML_POPUP.includes(layerBodId)) {
        const featureId = feature['featureId'];
        const lang = this.gettextCatalog.currentLanguage;
        const url = `https://api3.geo.admin.ch/rest/services/api/MapServer/${layerBodId}/${featureId}/htmlPopup?lang=${lang}`;
        this.http_.get(url, NO_HTTP_CREDENTIALS)
          .then(response => feature['properties']['htmlPopup'] = response.data);
      }
    }

    this.filterFeaturesByLayers_();
    this.orderFeatures_();

    // Normalize track features
    const trackFeaturesJSON = this.format_.writeFeaturesObject(trackFeatures);
    trackFeaturesJSON.features.forEach((feature) => {
      const props = feature['properties'];
      props['sl_title'] = props['name'] || props['title'];
      props['sl_colorclass'] = 'track-choice';
      if (feature.geometry.type === 'Point' && props['type'] !== 'track') {
        props['sl_subtitle'] = this.gettextCatalog.getString('Photo / Text') + ' ' +
            props['sort'];
        props['type'] = 'track_poi';
      } else {
        props['sl_subtitle'] = this.gettextCatalog.getString('My Tour');
        props['type'] = 'track';
      }
    });

    // Extend menu with picked track features
    this.features = trackFeaturesJSON.features.concat(this.features);

    // Extend menu with detour features
    const ftype = 'detour';
    if (filteredFeatures[ftype] && this.mainCtrl_.detourState) {
      filteredFeatures[ftype].forEach((feature) => {
        const props = feature['properties'];
        props['sl_title'] = props['title'];
        props['sl_colorclass'] = 'detour-choice';
        props['type'] = ftype;
        props['sl_name'] = this.gettextCatalog.getString('Detour');
        // pretend to have a line geometry so that the filtering below
        // is consistent with other line features
        feature.geometry = {type: 'DetourLine'};
      });
      this.features = filteredFeatures['detour'].concat(this.features);
    }

    this.filterFeaturesByGeometry_();

    this.features.forEach(f => {
      if (f.geometry && f.geometry.type === 'DetourLine') {
        // remove the fake geometry
        delete f.geometry;
      }
    });

    // don't display the short list if there's only one feature to show.
    if (this.features.length === 1) {
      this.scope_.$root.$broadcast('featureSelected', this.features[0]);
    } else {
      this.timeout_(this.showPopup_.bind(this));
    }
  }

  /**
   * @private
   * @param {Object} feature
   * @return {Object} //FIXME type
   */
  handleFeatureProperties_(feature) {
    const properties = feature['properties'];
    const land = properties['land'];
    const category = properties['category'];
    const layers = [];
    let title, subtitle, hlname, name, i, colorClass, url;

    //Case segment (lines)
    if (properties['type'] === 'etappen') {
      colorClass = LAND_COLORS_CLASS[land];
      hlname = WS_ALIASES[land];
      name = capitalize(WS_I18N[hlname]);
      // route number = decimals before comma
      // segment number = decimals after comma
      const numbers = properties['e_number'].split('.');
      console.assert(numbers.length === 2);
      const route_number = parseInt(numbers[0], 10);
      const segment_number = parseInt(numbers[1], 10);

      if (properties['route_panel_hasnumber']) {
        title = route_number + ' ' + properties['title'];
      } else {
        title = properties['title'];
      }
      subtitle = properties['start'] + '\u2014' + properties['end'];

      // special case for winter layers: the layer name contains no 'Etappen<category>'
      // but simply the layer name. It will change when these layers will contain more than
      // one category
      if (isSingleWinterLayer(/** @type {string} */ (land))) {
        layers.push(capitalize(land));
      } else {
        layers.push(capitalize(land) + 'Etappen' +
            capitalize(WS_ALIASES[category]));
      }
      const land_for_website = LAND_FOR_WEBSITE[land] || land;
      url = `https://www.schweizmobil.ch/${this.gettextCatalog.currentLanguage}/${land_for_website}`;
      if (!properties['route_has_segment']) {
        // only take the route number to build the url
        url += '/route' + route_number;
      } else {
        url += '/etappe' + properties['e_number'];
        subtitle = this.gettextCatalog.getString('Stage') + ' ' + segment_number +
            ' ' + subtitle;
      }
    } else if (!category) {
      // Case swisstopo
      const layername = BODID_TO_LAYERNAME[feature['layerBodId']];
      console.assert(layername);

      title = properties['label'];
      subtitle = this.gettextCatalog.getString(layername);

      if (layername === 'AlpguardDog') {
        properties['type'] = hlname = 'alpguarddog';
        name = this.gettextCatalog.getString(layername);
        layers.push(layername);
      } else {
        console.assert(['WildlifeArea', 'WildlifeReserve'].includes(layername));
        properties['type'] = hlname = 'wildlife';
        properties['wildlife_type'] = layername;
        name = this.gettextCatalog.getString('NatureLandscape');
        layers.push('WildlifeArea', 'WildlifeReserve');

        // no translation for 'en', fallback to 'de'
        const langCode = this.gettextCatalog.currentLanguage == 'en' ? 'de' : this.gettextCatalog.currentLanguage;
        properties['best'] = properties['best_' + langCode];
        properties['schutzs'] = properties['schutzs_' + langCode];
        properties['wegtyp'] = properties['wegtyp_' + langCode];

        title = properties['wegtyp'];
      }
    } else if (category === 'slowup') {
      // Case slowup
      colorClass = LAND_COLORS_CLASS[category];
      hlname = category;
      name = capitalize(category);
      layers.push(name);
      // change date format from yyyy-mm-dd to dd.mm.yyyy
      subtitle = properties['date'];
      const match = subtitle.match(/(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        subtitle = match[3] + '.' + match[2] + '.' + match[1];
      }
      title = properties['title'];
    } else if (category === 'swisspark') {
      colorClass = LAND_COLORS_CLASS[category];
      hlname = category;
      name = capitalize(category);
      layers.push(name);
      title = properties['title'];
      subtitle = this.gettextCatalog.getString(name);
      url = properties['url'];
    } else {
      // Case POI
      title = properties['title'] || properties['name'];

      //Case POI - Accomodations
      if (category === 'accomodation') {
        const types = properties['types'];
        const translatedTypes = [];
        for (i = 0; i < types.length; i++) {
          translatedTypes.push(
            this.gettextCatalog.getString(WS_I18N[types[i]])
          );
          layers.push(capitalize(types[i]));
        }
        hlname = types[0];
        name = WS_I18N[types[0]];
        subtitle = translatedTypes.join(', ');

      // Case POI - stations
      } else if (category === 'stations') {
        hlname = properties['sc_transport'];
        name = WS_I18N[hlname];
        layers.push(capitalize(hlname));
        subtitle = this.gettextCatalog.getString(name);
        colorClass = LAND_COLORS_CLASS[category];

      // Case POI - mountaineering
      } else if (category === 'mountaineering') {
        properties['sac_type'] = properties['sac_type'].includes('climbing') ? 'climbing' : properties['sac_type'];
        hlname = properties['sac_type'].replace('_', '');
        const layer = MOUNTAINEERING_TYPE_TO_LAYER[hlname];
        name = this.gettextCatalog.getString(layer);
        layers.push(layer.replace(' ', ''));
        subtitle = this.gettextCatalog.getString(name);
        colorClass = LAND_COLORS_CLASS[category];
        properties['sac_url'] = () => this.getMountaineeringUrl(properties);

        // Case POI (services and others)
      } else {
        console.assert(!category.includes(','));
        hlname = WS_ALIASES[category];
        layers.push(capitalize(hlname));
        name = capitalize(hlname);
        if (WS_I18N[hlname]) {
          name = capitalize(WS_I18N[hlname]);
        }
        subtitle = this.gettextCatalog.getString(name);
        const base = 'https://www.schweizmobil.ch/' + this.gettextCatalog.currentLanguage;
        if (category === 'places') {
          url = base + '/orte/rkey/' + properties['r_key'];
        } else if (category === 'orte' || hlname === 'sightseeing') {
          url = base + '/poi/' + properties['r_key'];
        }
      }

    }

    return {
      'sl_colorclass': colorClass || '',
      'sl_hlname': hlname,
      'sl_layers': layers,
      'sl_name': this.gettextCatalog.getString(name),
      'sl_subtitle': subtitle,
      'sl_title': title,
      'sl_url': url
    };
  }

  /**
   * @private
   * Filter by active layers (Keep only features from active layers)
   */
  filterFeaturesByLayers_() {
    const activeFeatures = [];
    let i, j, properties, layers;
    const activeLayers = this.mainCtrl_.activeLayers();
    for (i = 0; i < this.features.length; i++) {
      properties = this.features[i]['properties'];
      layers = properties['sl_layers'];
      for (j = 0; j < layers.length; j++) {
        if (layers[j] in activeLayers) {
          activeFeatures.push(this.features[i]);
          break;
        }
      }
    }
    this.features = activeFeatures;
  }

  /**
   * @private
   * Filter by geometry
   */
  filterFeaturesByGeometry_() {
    const pointFeatures = this.features.filter((feature) => feature.geometry.type === 'Point');
    if (pointFeatures.length > 0) {
      // only display the 'Point' features
      this.features = pointFeatures;
      return;
    }

    if (this.view_.getResolution() > 20) { // Show query results for the lines only for resolutions 1-20
      this.features = this.features.filter((feature) =>
        !(feature.geometry.type.includes('Line') && feature.properties.type === 'wildlife'));
    }

    // select only line on polygon-line click
    const notPolygonFeatures = this.features.filter(feature => !feature.geometry.type.includes('Polygon'));
    if (notPolygonFeatures.length > 0) {
      this.features = notPolygonFeatures;
    }

  }

  /**
   * @private
   * Order features by layer and by name
   */
  orderFeatures_() {
    this.features.sort(function(a, b) {
      const index_a = layerIndex(a['properties']['sl_layers'][0]);
      const index_b = layerIndex(b['properties']['sl_layers'][0]);
      const index_diff = index_a - index_b;

      if (index_diff === 0) {
        const name_a = a['properties']['sl_title'];
        const name_b = b['properties']['sl_title'];

        return name_a > name_b ? 1 : name_a < name_b ? -1 : 0;
      } else {
        return index_diff;
      }
    });
  }

  /**
   * @private
   */
  showPopup_() {
    this.popup_.setPositioning('bottom-left');
    this.popup_.setPosition(this.click2DCoordinates_);
    if (this.features.length === 0) {
      // hide the popup after 3 seconds if the list is empty
      this.timeout_(() => {
        this.popup_.setPosition(undefined);
      }, 3000);
    }
  }

  /**
   * @export
   */
  closePopup() {
    if (this.popup_.getPosition()) {
      // for detour and other special cases
      this.unsetHighlight_();
      this.features.length = 0;
      this.popup_.setPosition(undefined);
    }
  }

  /**
   * @private
   * @param {jQuery.Event} evt
   */
  onSelectFeature_(evt) {
    const featureId = $(evt.currentTarget).data('id');
    let i;

    for (i = 0; i < this.features.length; i++) {
      if (featureId === this.features[i]['id']) {
        break;
      }
    }

    this.scope_.$root.$broadcast('featureSelected', this.features[i]);
    this.closePopup();
  }

  getMountaineeringUrl(properties) {
    const lang = this.gettextCatalog.currentLanguage;
    const base = `https://www.sac-cas.ch/${lang}`;
    const hutAndTours = this.gettextCatalog.getString('huts-and-tours');
    const portal = this.gettextCatalog.getString('sac-route-portal');
    let type = '';
    switch (properties.sac_type) {
      case 'climbing':
        type = this.gettextCatalog.getString('sac_type_climbing');
        break;
      case 'mountain_hiking':
        type = this.gettextCatalog.getString('sac_type_mountain_hiking');
        break;
      case 'alpine_tour':
        type = this.gettextCatalog.getString('sac_type_alpine_tour');
        break;
      case 'via_ferrata':
        type = this.gettextCatalog.getString('sac_type_via_ferrata');
        break;
      case 'ski_tour':
        type = this.gettextCatalog.getString('sac_type_ski_tour');
        break;
      default:
        console.error('Missing sac_type');

    }
    const prepareTitle = (title) => {
      title = replaceSpecialCharacters(properties.title);
      title = title.replace(/[^a-zA-Z0-9\s]/g, '');
      title = title.toLowerCase().replace(/[\s]/g, '-');
      return title;
    };
    const title = prepareTitle(properties.title);
    const poiTitle = prepareTitle(properties.poi_title);
    return `${base}/${hutAndTours}/${portal}/${poiTitle}-${properties.sac_poi_id}/${type}/${title}-${properties.sac_orig_id}`;
  }
}

appBase.controller('appQueryController', QueryController);

/**
 * @return {angular.Directive} The directive specs.
 * @ngInject
 */
const queryDirective = function() {
  return {
    restrict: 'A',
    bindToController: {
      'active': '='
    },
    controller: 'appQueryController',
    controllerAs: 'queryCtrl',
    templateUrl: 'partials/query.html'
  };
};

appBase.directive('appQuery', queryDirective);

export default queryDirective;
